import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../API';
import styles from './UserPicker.module.css';

/**
 * GroupPicker — typeahead for selecting Girder groups.
 * Mirrors UserPicker: on focus shows list, typing refines, click outside closes.
 *
 * Props:
 *   onSelect     — called with the full group object when a group is chosen
 *   excludeIds   — array of group ID strings to omit from results
 *   placeholder  — input placeholder text
 *   autoFocus    — if true, focuses the input on mount
 */
function GroupPicker({ onSelect, excludeIds = [], placeholder = 'Search groups…', autoFocus = false }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchGroups = useCallback((text) => {
        setLoading(true);
        const queryParams = text ? { text, limit: 25 } : { limit: 25 };
        api.get('group', { query: queryParams })
            .then((r) => {
                if (r.status === 200) {
                    const list = Array.isArray(r.data) ? r.data : (r.data?.results ?? r.data ?? []);
                    const filtered = list.filter(
                        (g) => !excludeIds.includes(String(g._id))
                    );
                    setResults(filtered);
                    setOpen(true);
                }
            })
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [excludeIds]);

    function handleFocus() {
        if (!open) {
            fetchGroups(query.trim() || undefined);
        }
    }

    function handleChange(e) {
        const val = e.target.value;
        setQuery(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchGroups(val.trim() || undefined);
        }, 200);
    }

    function handleSelect(group) {
        onSelect(group);
        setQuery('');
        setResults([]);
        setOpen(false);
    }

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <input
                ref={inputRef}
                type="text"
                className={styles.input}
                value={query}
                onChange={handleChange}
                onFocus={handleFocus}
                placeholder={placeholder}
                autoComplete="off"
            />
            {loading && <span className={styles.loadingHint}>Loading…</span>}
            {open && results.length > 0 && (
                <ul className={styles.dropdown}>
                    {results.map((group) => (
                        <li key={group._id}>
                            <button
                                type="button"
                                className={styles.dropdownItem}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(group);
                                }}
                            >
                                {group.name || group._id}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {open && !loading && results.length === 0 && (
                <ul className={styles.dropdown}>
                    <li className={styles.noResults}>No matching groups.</li>
                </ul>
            )}
        </div>
    );
}

export default GroupPicker;
